import Logger from '@/lib/logger';

export const BUILD_RECOVERY_QUERY_PARAM = '__atlas_build_recovery';
const ATTEMPTED_BUILDS_STORAGE_KEY = 'atlas_build_recovery_attempted';
const TERMINAL_FALLBACK_DELAY_MS = 5_000;

export const CURRENT_BUILD_ID =
  typeof __BUILD_ID__ === 'string'
    ? __BUILD_ID__
    : typeof __APP_VERSION__ === 'string'
      ? __APP_VERSION__
      : 'development';

export interface BuildMetadata {
  buildId: string;
  version: string;
  builtAt: string;
  assets?: string[];
}

export type BuildRecoveryState =
  | { status: 'idle' }
  | {
      status: 'terminal';
      destination: string;
      currentBuildId: string;
      remoteBuildId?: string;
    };

type BuildRecoveryListener = () => void;
type RecoveryResult = 'reloading' | 'terminal';

const listeners = new Set<BuildRecoveryListener>();
const attemptedBuilds = new Set<string>();
let state: BuildRecoveryState = { status: 'idle' };
let pendingNavigationDestination: string | undefined;
let recoveryPromise: Promise<RecoveryResult> | undefined;
let initialized = false;

export class BuildRecoveryError extends Error {
  constructor() {
    super('A newer application build is required');
    this.name = 'BuildRecoveryError';
  }
}

export function isBuildRecoveryError(
  error: unknown
): error is BuildRecoveryError {
  return error instanceof BuildRecoveryError;
}

export function isAssetLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  return /dynamically imported module|importing a module script failed|loading chunk|chunkloaderror|chunk load failed|unable to preload css|failed to fetch dynamically imported module|vite:preloaderror|preload.*(?:css|script)/i.test(
    message
  );
}

export function getBuildRecoveryState(): BuildRecoveryState {
  return state;
}

export function subscribeToBuildRecovery(
  listener: BuildRecoveryListener
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publishState(nextState: BuildRecoveryState): void {
  state = nextState;
  for (const listener of listeners) {
    listener();
  }
}

function getCurrentDestination(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isBuildRecoveryParameter(segment: string): boolean {
  const [encodedKey] = segment.split('=', 1);
  try {
    return (
      decodeURIComponent(encodedKey.replace(/\+/g, ' ')) ===
      BUILD_RECOVERY_QUERY_PARAM
    );
  } catch {
    return false;
  }
}

function removeBuildRecoveryParameter(url: URL): string {
  const remainingQuery = url.search
    .slice(1)
    .split('&')
    .filter((segment) => segment && !isBuildRecoveryParameter(segment))
    .join('&');

  return `${url.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${url.hash}`;
}

function createRecoveryUrl(destination: string): string {
  const url = new URL(destination, window.location.origin);
  const marker = `${encodeURIComponent(BUILD_RECOVERY_QUERY_PARAM)}=${encodeURIComponent(CURRENT_BUILD_ID)}`;
  return `${url.origin}${url.pathname}${url.search}${url.search ? '&' : '?'}${marker}${url.hash}`;
}

export function setPendingNavigationDestination(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): void {
  pendingNavigationDestination = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

export function clearPendingNavigationDestination(): void {
  pendingNavigationDestination = undefined;
}

function persistAttemptedBuilds(): void {
  try {
    window.sessionStorage.setItem(
      ATTEMPTED_BUILDS_STORAGE_KEY,
      JSON.stringify(Array.from(attemptedBuilds))
    );
  } catch (error) {
    Logger.warn('Unable to persist the build recovery guard', error);
  }
}

function restoreAttemptedBuilds(): void {
  try {
    const serialized = window.sessionStorage.getItem(
      ATTEMPTED_BUILDS_STORAGE_KEY
    );
    if (!serialized) {
      return;
    }

    const parsed: unknown = JSON.parse(serialized);
    if (Array.isArray(parsed)) {
      for (const buildId of parsed) {
        if (typeof buildId === 'string') {
          attemptedBuilds.add(buildId);
        }
      }
    }
  } catch (error) {
    Logger.warn('Unable to restore the build recovery guard', error);
  }
}

export function initializeBuildRecovery(): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;

  restoreAttemptedBuilds();

  const url = new URL(window.location.href);
  const attemptedBuildId = url.searchParams.get(BUILD_RECOVERY_QUERY_PARAM);
  if (!attemptedBuildId) {
    return;
  }

  attemptedBuilds.add(attemptedBuildId);
  persistAttemptedBuilds();
  window.history.replaceState(
    window.history.state,
    '',
    removeBuildRecoveryParameter(url)
  );
}

async function fetchRemoteBuildMetadata(): Promise<BuildMetadata | undefined> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(3_000),
    });
    if (!response.ok) {
      return undefined;
    }

    const metadata: unknown = await response.json();
    if (
      typeof metadata === 'object' &&
      metadata !== null &&
      'buildId' in metadata &&
      typeof metadata.buildId === 'string' &&
      'version' in metadata &&
      typeof metadata.version === 'string' &&
      'builtAt' in metadata &&
      typeof metadata.builtAt === 'string'
    ) {
      return metadata as BuildMetadata;
    }
  } catch (error) {
    Logger.warn('Unable to read remote build metadata during recovery', error);
  }

  return undefined;
}

function publishTerminalState(
  destination: string,
  remoteBuildId?: string
): void {
  publishState({
    status: 'terminal',
    destination,
    currentBuildId: CURRENT_BUILD_ID,
    ...(remoteBuildId ? { remoteBuildId } : undefined),
  });
}

async function performRecovery(error: unknown): Promise<RecoveryResult> {
  const destination = pendingNavigationDestination ?? getCurrentDestination();

  if (attemptedBuilds.has(CURRENT_BUILD_ID)) {
    Logger.error('Asset loading failed after automatic build recovery', {
      buildId: CURRENT_BUILD_ID,
      destination,
      error,
    });
    publishTerminalState(destination);
    return 'terminal';
  }

  attemptedBuilds.add(CURRENT_BUILD_ID);
  persistAttemptedBuilds();
  const remoteMetadata = await fetchRemoteBuildMetadata();

  Logger.error('Asset loading failed; attempting one build recovery', {
    currentBuildId: CURRENT_BUILD_ID,
    destination,
    error,
    remoteBuildId: remoteMetadata?.buildId,
  });

  const recoveryUrl = createRecoveryUrl(destination);

  try {
    window.location.replace(recoveryUrl);
  } catch (replaceError) {
    Logger.error('Unable to navigate during build recovery', replaceError);
    publishTerminalState(destination, remoteMetadata?.buildId);
    return 'terminal';
  }

  window.setTimeout(() => {
    publishTerminalState(destination, remoteMetadata?.buildId);
  }, TERMINAL_FALLBACK_DELAY_MS);

  return 'reloading';
}

export function recoverFromAssetLoadError(
  error: unknown
): Promise<RecoveryResult> {
  if (!isAssetLoadError(error)) {
    return Promise.reject(error);
  }

  if (!recoveryPromise) {
    recoveryPromise = performRecovery(error).finally(() => {
      recoveryPromise = undefined;
    });
  }

  return recoveryPromise;
}

export function handleVitePreloadError(event: Event): void {
  event.preventDefault();
  const payload =
    'payload' in event
      ? (event as Event & { payload?: unknown }).payload
      : undefined;
  void recoverFromAssetLoadError(
    isAssetLoadError(payload) ? payload : new Error('vite:preloadError')
  );
}

export function retryBuildRecovery(): void {
  const destination =
    state.status === 'terminal' ? state.destination : getCurrentDestination();
  attemptedBuilds.delete(CURRENT_BUILD_ID);
  persistAttemptedBuilds();
  publishState({ status: 'idle' });
  window.location.replace(
    new URL(destination, window.location.origin).toString()
  );
}

export function resetBuildRecoveryForTests(): void {
  listeners.clear();
  attemptedBuilds.clear();
  state = { status: 'idle' };
  pendingNavigationDestination = undefined;
  recoveryPromise = undefined;
  initialized = false;
}
